import { LitElement, css, html, nothing, type PropertyValues } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { createRef, ref, type Ref } from "lit/directives/ref.js";
import type { ChatState } from "../types.js";
import {
  HEADER_PADDING_END,
  HEADER_PADDING_START,
  OVERVIEW_CONTROL_INSET,
  PANEL_STACK_GAP,
  PANEL_TEXT_INSET,
} from "../conversations-panel-metrics.js";
import {
  HelpConversationProvider,
  type ConversationProviderHost,
} from "../conversation-provider.js";

const COLLAPSED_STORAGE_KEY = "help-jonas:conversations-collapsed";

/**
 * Everything Help changes inside `afas-chat-overview`, in one place so the whole set can be
 * dropped when the component offers a supported lever for it. See `applyOverviewOverrides`.
 */
const OVERVIEW_OVERRIDES = `
  .search { display: none; }
  afas-container::part(content) { padding-inline: 0; scrollbar-width: thin; }
  .group-label { padding-inline: ${PANEL_TEXT_INSET}px; }
`;

/** The same, for the shadow root of a single conversation row. */
const OVERVIEW_ITEM_OVERRIDES = `
  .entry { padding-inline: ${PANEL_TEXT_INSET}px; border-radius: 0; }
`;

/** Overview shadow roots that already carry the sheet below, so it is injected once each. */
const overviewOverrideRoots = new WeakSet<ShadowRoot>();

/** Conversation-item shadow roots that already carry their own override sheet. */
const itemOverrideRoots = new WeakSet<ShadowRoot>();

/** Below this width the workspace has no room for a persistent sidebar. */
const COMPACT_VIEWPORT_QUERY = "(max-width: 760px)";

@customElement("help-jonas-conversations-panel")
export class HelpJonasConversationsPanel extends LitElement {
  static override styles = css`
    :host {
      display: flex;
      flex: 0 0 auto;
      flex-direction: column;
      height: 100%;
      min-height: 0;
      width: 100%;
      box-sizing: border-box;
      border-right: 1px solid var(--color-foreground-300);
      background: var(--color-background-900);
    }

    /* Mirrors RAIL_WIDTH in jonas-chat-shell.ts. */
    :host([collapsed]) {
      width: 56px;
    }

    .header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 8px;
      flex: 0 0 auto;
      box-sizing: border-box;
      /* Derived so the title lines up with the group labels and the collapse button ends
         where the "new chat" button does; see conversations-panel-metrics.ts. The bottom
         is the step to the list, which the body can no longer carry itself. */
      padding: 8px ${HEADER_PADDING_END}px ${PANEL_STACK_GAP}px
        ${HEADER_PADDING_START}px;
    }

    .header-title {
      overflow: hidden;
      font-weight: 600;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .rail {
      display: flex;
      flex-direction: column;
      align-items: center;
      padding: 8px 0;
    }

    /*
     * Folding the panel must not move the icons, so the rail repeats the expanded column:
     * the header's 8px above the collapse button and its 8px below, before "new chat".
     * The conversations button keeps that same step -- it stands in for the list, which
     * is given more air than an icon needs.
     */
    .rail afas-button + afas-button,
    .rail afas-dropdown-button {
      margin-top: ${PANEL_STACK_GAP}px;
    }

    .body {
      display: flex;
      flex: 1 1 auto;
      flex-direction: column;
      min-height: 0;
      /* afas-chat-overview brings no spacing of its own -- it fills the box it is given --
         so the space between the button and the list belongs to the box that stacks them. */
      gap: 16px;
      /* No inset of its own: the overview's scroll box has to reach the divider, so its
         scrollbar sits against it instead of floating 8px short. The rows keep the inset
         that the overview applies to its own content. */
    }

    afas-chat-overview {
      flex: 1 1 auto;
      min-height: 0;
    }

    /* Lines up with the search field and the rows inside the overview. */
    .new-chat {
      display: block;
      flex: 0 0 auto;
      margin: 0 ${OVERVIEW_CONTROL_INSET}px;
    }

    .new-chat::part(button) {
      width: 100%;
      justify-content: center;
    }

    /*
     * Centre the label on the button instead of the icon-and-label pair: a negative pull of
     * exactly the icon box takes the icon out of that centring, so it hangs to the left of
     * the centred text while keeping the gap afas-button puts between the two. The same
     * var(--icon-size, 16px) the button sizes its icon with, so the two cannot drift.
     */
    .new-chat afas-icon[slot="icon"] {
      margin-left: calc(-1 * var(--icon-size, 16px));
    }

    .rail-popup-overview {
      display: block;
      width: 320px;
      max-height: 60vh;
    }

    .history-skeleton {
      display: flex;
      flex-direction: column;
      gap: 6px;
      padding: 4px 2px;
    }

    .history-error {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      gap: 8px;
    }

    /* The conversations panel (history sidebar) must never appear on paper, and
       hiding it here also removes a Ctrl+P trailing blank page: when the chat
       shell is released to height:auto for print, this panel — freed from its
       on-screen height:100% — would otherwise expand to its full (unscrolled)
       history height and print as a blank trailing page. Media-only :host rule
       so it applies reliably during Chromium's interactive print pass. */
    @media print {
      :host {
        display: none;
      }
    }
  `;

  @property({ attribute: false })
  accessor state: ChatState | null = null;

  /**
   * Set by the shell when the chat column could not keep its minimum width next
   * to the open source panel even with this panel at its narrowest. The panel
   * then folds to its rail exactly as on a compact viewport: the stored
   * preference is left alone and wins again once there is room.
   */
  @property({ type: Boolean })
  accessor squeezed = false;

  @state()
  private accessor collapsed = false;

  private readonly compactQuery = globalThis.matchMedia?.(COMPACT_VIEWPORT_QUERY);

  private readonly compactListener = (): void => {
    this.applyForcedOrStored();
  };

  /** A viewport too narrow for a sidebar, or a chat column squeezed by the source panel. */
  private get forcedCollapsed(): boolean {
    return Boolean(this.compactQuery?.matches) || this.squeezed;
  }

  // Room (or the lack of it) wins over the stored preference, but leaves it
  // untouched so widening the window restores what the user chose.
  private applyForcedOrStored(): void {
    this.applyCollapsed(this.forcedCollapsed ? true : this.storedCollapsed());
  }

  private readonly popupRef: Ref<HTMLElement & { open: boolean }> = createRef();

  private readonly provider = new HelpConversationProvider({
    resumeConversation: (index) => this.resume(index),
    removeConversation: (sessionToken) => this.removeEntry(sessionToken),
  } satisfies ConversationProviderHost);

  override connectedCallback(): void {
    super.connectedCallback();
    this.collapsed = this.forcedCollapsed ? true : this.storedCollapsed();
    this.reflectCollapsed();
    this.compactQuery?.addEventListener("change", this.compactListener);
  }

  override disconnectedCallback(): void {
    this.compactQuery?.removeEventListener("change", this.compactListener);
    this.provider.dispose();
    super.disconnectedCallback();
  }

  protected override firstUpdated(): void {
    // Announce the restored collapsed state so the shell can drop the inline
    // width/resizer for the narrow rail (the panel owns the collapsed flag).
    this.emitCollapsedState();
  }

  protected override willUpdate(changed: PropertyValues<this>): void {
    this.provider.setState(this.state);
    // The shell flips `squeezed` from its resize observer; the first render
    // already took it into account in connectedCallback.
    if (changed.has("squeezed") && changed.get("squeezed") !== undefined) {
      this.applyForcedOrStored();
    }
  }

  protected override updated(): void {
    void this.applyOverviewOverrides();
  }

  /**
   * Bends `afas-chat-overview` into the design: it draws a search field the design does not
   * have, insets and rounds its rows so they cannot run full width, and starts its labels
   * two columns away from the panel header. None of that has a property, slot or part, and
   * all of it sits in a shadow root — so a stylesheet in that root is the only lever, the
   * same approach the shell uses for the designer layout's print rules. (Checked against
   * 5.0.24: the speech-bubble icon that 2.x drew before every row is gone, the rest is not.)
   *
   * The whole set lives in OVERVIEW_OVERRIDES and OVERVIEW_ITEM_OVERRIDES, so a component
   * version that supports these can drop both constants and this method with it. The row
   * rules live in each item's own root rather than the overview's, hence the inner loop.
   * Fails open: if an internal class name ever changes, that rule simply stops applying.
   */
  private async applyOverviewOverrides(): Promise<void> {
    if (typeof CSSStyleSheet === "undefined") return;

    for (const overview of this.renderRoot.querySelectorAll("afas-chat-overview")) {
      // The overview renders its list asynchronously after the provider signals a change, so
      // wait for it or a freshly added conversation would keep its own inset until the next
      // update.
      await (overview as LitElement).updateComplete;

      const root = overview.shadowRoot;
      if (!root) continue;

      this.injectSheet(root, overviewOverrideRoots, OVERVIEW_OVERRIDES);
      for (const item of root.querySelectorAll("afas-chat-overview-item")) {
        if (item.shadowRoot) {
          this.injectSheet(item.shadowRoot, itemOverrideRoots, OVERVIEW_ITEM_OVERRIDES);
        }
      }
    }
  }

  private injectSheet(
    root: ShadowRoot,
    seen: WeakSet<ShadowRoot>,
    css: string,
  ): void {
    if (seen.has(root) || !("adoptedStyleSheets" in root)) return;
    try {
      const sheet = new CSSStyleSheet();
      sheet.replaceSync(css);
      root.adoptedStyleSheets = [...root.adoptedStyleSheets, sheet];
      seen.add(root);
    } catch {
      // Best-effort; the list stays usable with the component's own chrome visible.
    }
  }

  protected override render() {
    if (!this.state) return nothing;
    return this.collapsed ? this.renderRail() : this.renderExpanded();
  }

  private renderExpanded() {
    return html`
      <div class="header">
        <span class="header-title">
          ${this.getLabel("conversationsTitle", "Gesprekken")}
        </span>
        ${this.renderIconButton(
          "SideNavigationOutlined",
          this.getLabel("conversationsCollapse", "Gesprekken inklappen"),
          () => this.toggleCollapsed(),
        )}
      </div>
      <div class="body">${this.renderHistory()}</div>
    `;
  }

  private renderRail() {
    // While the shell reports the chat squeezed, expanding would hand the chat
    // back a column of some 300px and store that as the preference, and nothing
    // would fold the panel again until the window changed. The fly-out below
    // keeps the conversations reachable, so the expand control simply goes
    // until there is room for it (unlike a compact viewport, where a deliberate
    // expand has always been allowed).
    return html`
      <div class="rail">
        ${this.squeezed
          ? nothing
          : this.renderIconButton(
              "SideNavigationOutlined",
              this.getLabel("conversationsExpand", "Gesprekken uitklappen"),
              () => this.toggleCollapsed(),
            )}
        ${this.renderIconButton(
          "AddOutlined",
          this.getLabel("newChat", "Nieuw gesprek"),
          () => this.newChat(),
          "primary",
        )}
        ${this.renderConversationsPopup()}
      </div>
    `;
  }

  /** Mirrors the collapsed-rail fly-out of afas-chat-layout, which Help does not use. */
  private renderConversationsPopup() {
    const label = this.getLabel("conversationsTitle", "Gesprekken");
    return html`
      <afas-dropdown-button
        ${ref(this.popupRef)}
        variant="minimal"
        tone="neutral"
        icon-only
        no-icon
        label=${label}
        tooltip=${label}
        placement="right-start"
      >
        <afas-icon slot="icon" icon="ChatOutlined"></afas-icon>
        <afas-chat-overview
          class="rail-popup-overview"
          compact
          .dataProvider=${this.provider}
          @conversation-selected=${() => this.closePopup()}
        ></afas-chat-overview>
      </afas-dropdown-button>
    `;
  }

  private renderHistory() {
    const status = this.state?.historyStatus ?? "ready";
    const hasEntries = (this.state?.history?.length ?? 0) > 0;
    // afas-chat-overview reads neither `loaded` nor `error`; it would render its own empty
    // state while history is still loading or has failed. Keep the skeleton and the
    // retryable error here, and only hand over once there is something settled to list.
    if (!hasEntries && status === "loading") return this.renderSkeleton();
    if (!hasEntries && status === "error") return this.renderHistoryError();
    return html`
      <afas-button
        class="new-chat"
        variant="primary"
        label=${this.getLabel("newChat", "Nieuw gesprek")}
        @click=${() => this.newChat()}
      >
        <afas-icon slot="icon" icon="AddOutlined"></afas-icon>
      </afas-button>
      <afas-chat-overview
        compact
        .dataProvider=${this.provider}
      ></afas-chat-overview>
    `;
  }

  private renderSkeleton() {
    const loadingLabel = this.getLabel("historyLoading", "Gesprekken laden");
    return html`
      <div
        class="history-skeleton"
        role="status"
        aria-busy="true"
        aria-label=${loadingLabel}
      >
        ${[0, 1, 2, 3].map(
          () =>
            html`<afas-skeleton
              variant="rectangle"
              width="100%"
              height="32px"
            ></afas-skeleton>`,
        )}
      </div>
    `;
  }

  private renderHistoryError() {
    const message = this.getLabel(
      "historyLoadErrorMessage",
      "We konden je gesprekken niet laden. Probeer het opnieuw.",
    );
    const retryLabel = this.getLabel("historyRetry", "Opnieuw proberen");
    return html`
      <afas-alert type="danger" data-ai-chat-history-error>
        <div class="history-error">
          <span>${message}</span>
          <afas-button
            variant="default"
            label=${retryLabel}
            @click=${this.retryHistory}
          ></afas-button>
        </div>
      </afas-alert>
    `;
  }

  /** The primary variant mirrors the filled "new chat" button the overview renders when
   * expanded; the rest of the rail stays quiet and neutral. */
  private renderIconButton(
    icon: string,
    label: string,
    onClick: () => void,
    variant: "minimal" | "primary" = "minimal",
  ) {
    return html`
      <afas-button
        variant=${variant}
        tone=${variant === "primary" ? "default" : "neutral"}
        icon-only
        label=${label}
        tooltip=${label}
        @click=${onClick}
      >
        <afas-icon slot="icon" icon=${icon}></afas-icon>
      </afas-button>
    `;
  }

  private closePopup(): void {
    if (this.popupRef.value) this.popupRef.value.open = false;
  }

  private newChat(): void {
    this.closePopup();
    this.dispatchEvent(
      new CustomEvent("jonas-new-chat", { bubbles: true, composed: true }),
    );
  }

  private resume(index: number): void {
    this.dispatchEvent(
      new CustomEvent("jonas-history-resume", {
        detail: { index },
        bubbles: true,
        composed: true,
      }),
    );
  }

  private removeEntry(sessionToken: string): Promise<void> {
    if (!sessionToken) return Promise.resolve();
    this.dispatchEvent(
      new CustomEvent("jonas-history-remove", {
        detail: { sessionToken },
        bubbles: true,
        composed: true,
      }),
    );
    // The app opens its confirmation dialog and updates the store when that settles; the
    // list then refreshes through the provider, not by resolving this promise later.
    return Promise.resolve();
  }

  private retryHistory(): void {
    this.dispatchEvent(
      new CustomEvent("jonas-history-retry", { bubbles: true, composed: true }),
    );
  }

  private toggleCollapsed(): void {
    this.applyCollapsed(!this.collapsed);
    try {
      globalThis.localStorage?.setItem(
        COLLAPSED_STORAGE_KEY,
        this.collapsed ? "1" : "0",
      );
    } catch {
      // Persistence is best-effort in privacy modes.
    }
  }

  private applyCollapsed(collapsed: boolean): void {
    if (this.collapsed === collapsed) return;
    this.collapsed = collapsed;
    this.reflectCollapsed();
    this.emitCollapsedState();
  }

  private storedCollapsed(): boolean {
    try {
      return globalThis.localStorage?.getItem(COLLAPSED_STORAGE_KEY) === "1";
    } catch {
      return false;
    }
  }

  // The collapsed flag drives the host width (56px rail). The shell sets an
  // inline width for the expanded panel that would otherwise win over the
  // collapsed CSS, so it needs to know when to stop applying it.
  private emitCollapsedState(): void {
    this.dispatchEvent(
      new CustomEvent("jonas-conversations-collapsed", {
        detail: { collapsed: this.collapsed },
        bubbles: true,
        composed: true,
      }),
    );
  }

  private reflectCollapsed(): void {
    this.toggleAttribute("collapsed", this.collapsed);
  }

  private getLabel(key: string, fallback: string): string {
    const value = this.state?.labels?.[key];
    return typeof value === "string" && value.trim() ? value : fallback;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "help-jonas-conversations-panel": HelpJonasConversationsPanel;
  }
}
